

































































































import Vue from 'vue';
import { required, sameAs, maxLength } from 'vuelidate/lib/validators';
import { emailAddress } from '@/validations';

import {
  CloseFullscreenWrapper,
  FormValidation,
  OpenHelp,
  LoadingMessage,
  TriggerRefreshError,
} from '@/mixins';

import {
  FullscreenWrapper,
  FullscreenSuccess,
  FullscreenError,
  FullscreenLoading,
} from '@/components/fullscreen-wrapper-components';

import { ValidateEmail } from '@/components/validate-email-component';

import { EventBus } from '@/event-bus';

import { api, MEMBER_API_SUB_URLS } from '@/api';
import { ExtendMixins } from '@/services';
import { AccountUpdateEmailFormModel, MemberPersonalInformationResponseModel } from '@/@types/member';

interface Data {
  isSubmitLoading: boolean;
  showSubmitSuccessMessage: boolean;
  successMessage: string;
  showSubmitErrorMessage: boolean;
  errorMessage: string;
  refreshError: boolean;
  validateEmailIsLoading: boolean;
  validateEmailModal: boolean;
  showValidateEmailSuccessMessage: boolean;
  showValidateEmailErrorMessage: boolean;
  confirmBeforeClose: boolean;
  changeMade: boolean;
  loadingMessage: string;
  formModel: AccountUpdateEmailFormModel;
}

export default ExtendMixins(
  CloseFullscreenWrapper,
  FormValidation,
  OpenHelp,
  LoadingMessage,
  TriggerRefreshError,
).extend({
  name: 'UpdateEmail',
  props: {
    params: {
      type: Object as () => any,
      required: true,
    },
  },
  data(): Data {
    return {
      isSubmitLoading: false,
      showSubmitSuccessMessage: false,
      successMessage: this.params.successMessage,
      showSubmitErrorMessage: false,
      errorMessage: this.params.errorMessage,
      refreshError: false,
      validateEmailIsLoading: false,
      validateEmailModal: false,
      showValidateEmailSuccessMessage: false,
      showValidateEmailErrorMessage: false,
      confirmBeforeClose: true,
      changeMade: false,
      loadingMessage: this.$options.filters.t([
        'validate-email_text_loading-message',
        'global_loading',
      ]),
      formModel: {
        token: '',
        emailAddress: '',
        confirmEmailAddress: '',
      },
    };
  },
  validations: {
    formModel: {
      token: {
        required,
      },
      emailAddress: {
        required,
        emailAddress,
        maxLength: maxLength(120),
        notSameAsExisting(value): boolean {
          return value !== this.personalInformation.email;
        },
      },
      confirmEmailAddress: {
        required,
        sameAs: sameAs('emailAddress'),
      },
    },
  },
  beforeMount() {
    this.constructShowErrorModel('formModel');
  },
  computed: {
    personalInformation(): MemberPersonalInformationResponseModel {
      return this.$store.getters.personalInformation;
    },

    // VALIDATIONS
    tokenErrors(): string[] {
      const formField = 'formModel.token';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'global_form-error_token_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
      ]);
    },
    emailErrors(): string[] {
      const formField = 'formModel.emailAddress';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'update-email_form-error_email_required',
            'global_form-error_email_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          [
            'update-email_form-error_email_invalid-format',
            'global_form-error_default_invalid-format',
          ],
          'emailAddress',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'update-email_form-error_email_length',
          'maxLength',
          { key: 'maxLength', val: 'max' },
        ),
        this.FormValidationMixin.validateField(
          formField,
          'update-email_form-error_existing_email',
          'notSameAsExisting',
        ),
      ]);
    },
    confirmEmailErrors(): string[] {
      const formField = 'formModel.confirmEmailAddress';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'update-email_form-error_confirm-email_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'update-email_form-error_confirm-email_same-as',
          'sameAs',
        ),
      ]);
    },
  },
  mounted() {
    EventBus.$on('updateAndValidateEmail', (val) => {
      this.formModel = Object.assign(this.formModel, { emailAddress: val });
      this.validateEmail();
    });
  },
  destroyed() {
    EventBus.$off('updateAndValidateEmail');
  },
  methods: {
    tryAgain(): void {
      if (this.errorMessage === this.params.errorMessageTokenInvalid) {
        // We need user to start over and request a new token
        this.closeFullscreenWrapper();
        return;
      }
      this.showSubmitErrorMessage = false;
      this.confirmBeforeClose = true;
      this.loadingMessage = this.$options.filters.t([
        'validate-email_text_loading-message',
        'global_loading',
      ]);
      this.errorMessage = this.params.errorMessage;
    },
    validateEmail(): void {
      this.validateEmailIsLoading = true;
      this.isSubmitLoading = true;
    },
    emailCancel(): void {
      this.isSubmitLoading = false;
      this.validateEmailIsLoading = false;
    },
    updateEmail(email): void {
      let confirmedEmail = this.formModel.emailAddress;

      if (email) {
        confirmedEmail = email;
      }

      this.validateEmailIsLoading = false;
      this.isSubmitLoading = true;
      this.changeLoadingMessage(
        (this.$refs.fullscreenLoadingComponent as any).$refs.loadingMessage,
        'loadingMessage',
        this.$options.filters.t([
          'update-email_submit_text_loading-message',
          'global_loading',
        ]),
      );
      api
        .put(
          `${MEMBER_API_SUB_URLS.members}/${
            this.$store.getters.user.memberId
          }/profiles/emails`,
          {
            emailAddress: confirmedEmail,
          },
          {
            headers: {
              'x-token': this.formModel.token,
            },
          },
        )
        .then(() => {
          this.refreshPersonalInformation();

          // GTM Event - Update Email
          window.dataLayer.push({
            event: 'updateEmail',
          });
        })
        .catch((error) => {
          if (error.status === 403) {
            this.errorMessage = this.params.errorMessageTokenInvalid;
          }

          this.confirmBeforeClose = false;
          this.showSubmitErrorMessage = true;
          this.isSubmitLoading = false;
        });
    },
    refreshPersonalInformation(): void {
      this.$store
        .dispatch('getPersonalInformation')
        .then(() => {
          this.confirmBeforeClose = false;
          this.showSubmitSuccessMessage = true;
          this.isSubmitLoading = false;
        })
        .catch(() => {
          this.isSubmitLoading = false;
          this.triggerRefreshError({
            message: [
              'update-email_text_refresh-data-error-message',
              'global_connection-error',
            ],
          });
        });
    },

    closeModal(): void {
      this.validateEmailModal = false;
    },
  },
  components: {
    FullscreenWrapper,
    FullscreenSuccess,
    FullscreenError,
    FullscreenLoading,
    ValidateEmail,
  },
});
